import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWorm,
  faDove,
  faRoad,
  faArrowsLeftRight,
  faArrowsUpDown,
  faCircleQuestion,
  faFlag,
  faLayerGroup,
  faListOl,
} from '@fortawesome/free-solid-svg-icons';
import { getGameTypePreview } from './GameTypePreviewLibrary';

// Category for grouping in create campaign and template gallery. Use GAME_CATEGORY_LABELS for display.
export const GAME_CATEGORIES = [
  { id: 'arcade', label: 'Arcade' },
  { id: 'trivia', label: 'Trivia' },
];

// Allowed character perspectives per game type. Used by the asset picker and
// the AI campaign generator to keep the picker (and the AI's choices) focused
// on the right kind of character art. Value is a single string, an array of
// allowed perspectives, or null for no constraint.
//   - 'front'  = facing the camera (static-style characters — Sky Bounce)
//   - 'side'   = profile / left-right view (Lane Dash, Jump Runner, Flap & Fly — most animated walkers)
//   - 'behind' = true back-of-character view (none in library yet)
//   - null     = no constraint (game type doesn't pick a character)
// Mirror this on the server in functions/index.js GAME_TYPE_PERSPECTIVE.
export const GAME_TYPE_PERSPECTIVE = {
  snake: null,
  runner: 'behind',
  sidescroller: 'side',
  verticalclimber: 'front',
  flappy: 'side',
  quizrush: null,
  completelist: null,
  minigolf: null,
  stacker: null,
};

// Background scroll orientation per game type. Used by pickers to hide
// horizon-composed side-scroller bgs from vertical games (Lane Dash, Sky
// Bounce, Stack Tower) and vice versa. An explicit `bgOrientation` on the
// gameType entry wins; otherwise derive from `backgroundDirection`
// ('down'/'up' → vertical, 'left'/'right' → horizontal). Games like Stack
// Tower have no scrolling parallax (backgroundDirection: null) but compose
// vertically, so they declare bgOrientation: 'vertical' explicitly.
export function getBackgroundOrientation(gameTypeId) {
  const t = gameTypes.find((g) => g.id === gameTypeId);
  if (t?.bgOrientation) return t.bgOrientation;
  const dir = t?.backgroundDirection;
  if (dir === 'down' || dir === 'up') return 'vertical';
  if (dir === 'left' || dir === 'right') return 'horizontal';
  return null;
}

// Preview media (image, video, example) per game type come from GameTypePreviewLibrary.
export const gameTypes = [
  {
    id: "sidescroller",
    category: 'arcade',
    backgroundDirection: 'left',
    perspective: 'side',
    name: "Jump Runner",
    description: "Tap to jump. Time it right or crash.",
    icon: ({ className }) => (
      <div className={className}>
        <FontAwesomeIcon icon={faArrowsLeftRight} style={{ width: '100%', height: '100%' }} />
      </div>
    ),
    options: {
      title: "Jump Runner",
      intro: "Tap to jump. Time obstacles right and grab stars on the way for bonus points.",
      ctaEnabled: false,
      playerImage: null,
      playerEmoji: null,
      playerAnimationFrames: [
        '/assets/library/players/new-characters-svg/character_green_walk_a.svg',
        '/assets/library/players/new-characters-svg/character_green_walk_b.svg',
      ],
      playerJumpImage: '/assets/library/players/new-characters-svg/character_green_jump.svg',
      playerSetName: 'Default character',
      obstacleImage: '/assets/library/obstacles/new-svg/spikes.svg',
      obstacleImageName: 'Spikes',
      obstacleEmoji: null,
      backgroundImage: 'procedural:skyClouds',
      backgroundImageName: 'Sky & Clouds',
      staticBackground: false,
      collectibleImage: '/assets/library/items/new-svg/star.svg',
      collectibleImageName: 'Star',
      platformImageLeft: '/assets/library/parse-tiles-svg/procedural_grass_horizontal_left.svg',
      platformImageMiddle: '/assets/library/parse-tiles-svg/procedural_grass_horizontal_middle.svg',
      platformImageRight: '/assets/library/parse-tiles-svg/procedural_grass_horizontal_right.svg',
      platformSetName: 'Grass',
      collectSound: 'synth:collect-coin',
      jumpSound: 'synth:jump-whoosh',
      deathSound: '/assets/audio/kenney-impact/impactGeneric_light_000.ogg',
      // Default colors will be set from theme when game is created
      // These are fallback defaults if theme colors aren't available
      theme: {
        colors: {
          game: {
            playerColor: null, // Will use customColors.primaryAction from theme
            obstacleColor: null, // Will use customColors.primary from theme
            backgroundColor: null, // Will use customColors.surface from theme
            groundColor: null, // Will use customColors.background from theme
            scoreText: null, // Will use customColors.text from theme
          }
        }
      },
      leaderboard: {
        enabled: true,
        emailRequired: 'optional',
      },
    },
  },
  {
    id: "verticalclimber",
    category: 'arcade',
    backgroundDirection: 'down',
    name: "Sky Bounce",
    description: "Hop platform to platform. Don't fall!",
    icon: ({ className }) => (
      <div className={className}>
        <FontAwesomeIcon icon={faArrowsUpDown} style={{ width: '100%', height: '100%' }} />
      </div>
    ),
    options: {
      title: "Sky Bounce",
      intro: "Hop from platform to platform. How high can you climb?",
      ctaEnabled: false,
      playerImage: '/assets/library/players/new-characters-svg/character_green_front.svg',
      playerSetName: 'Default character',
      playerEmoji: null,
      backgroundImage: 'procedural:skyJourney',
      backgroundImageName: 'Sky Journey',
      platformImageLeft: '/assets/library/parse-tiles-svg/procedural_grass_horizontal_left.svg',
      platformImageMiddle: '/assets/library/parse-tiles-svg/procedural_grass_horizontal_middle.svg',
      platformImageRight: '/assets/library/parse-tiles-svg/procedural_grass_horizontal_right.svg',
      projectileImage: '/assets/library/items/new-svg/star.svg',
      collectibleImage: '/assets/library/items/new-svg/star.svg',
      collectibleImageName: 'Star',
      springImage: '/assets/library/items/new-svg/spring.png',
      springImageName: 'Spring',
      obstacleImage: '/assets/library/obstacles/new-svg/fly_a.svg',
      obstacleImageName: 'Fly',
      collectSound: 'synth:collect-coin',
      jumpSound: 'synth:jump-whoosh',
      shootSound: '/assets/audio/new-sounds/sfx_laser1.ogg',
      deathSound: '/assets/audio/kenney-impact/impactGeneric_light_000.ogg',
      leaderboard: {
        enabled: true,
        emailRequired: 'optional',
      },
    },
  },
  {
    id: "flappy",
    category: 'arcade',
    backgroundDirection: 'left',
    perspective: 'side',
    name: 'Flap & Fly',
    description: 'Tap through the gaps. One hit and it\'s over.',
    icon: ({ className }) => (
      <div className={className}>
        <FontAwesomeIcon icon={faDove} style={{ width: '100%', height: '100%' }} />
      </div>
    ),
    options: {
      title: "Flap & Fly",
      intro: "Tap to flap through the gaps. One hit and it's over. How far can you go?",
      ctaEnabled: false,
      backgroundImage: 'procedural:skyClouds',
      backgroundImageName: 'Sky & Clouds',
      staticBackground: false,
      playerAnimationFrames: [
        '/assets/library/players/new-characters-svg/character_green_walk_a.svg',
        '/assets/library/players/new-characters-svg/character_green_walk_b.svg',
      ],
      playerJumpImage: '/assets/library/players/new-characters-svg/character_green_jump.svg',
      playerSetName: 'Default character',
      collectibleImage: '/assets/library/items/new-svg/star.svg',
      collectibleImageName: 'Star',
      pipeImage: '/assets/library/parse-tiles-svg/procedural_pipe_industrial_vertical_body.svg',
      pipeImageName: 'Industrial Pipe',
      pipeColor: '#4B5563',
      collectSound: 'synth:collect-pop',
      flapSound: 'synth:laneswitch-swoosh',
      deathSound: '/assets/audio/kenney-impact/impactGeneric_light_000.ogg',
      leaderboard: {
        enabled: true,
        emailRequired: 'optional',
        allowMultipleAttempts: true
      },
      gameSettings: {
        pipeColor: '#4B5563',
      },
    },
  },
  {
    id: "runner",
    category: 'arcade',
    backgroundDirection: 'down',
    perspective: 'behind',
    name: 'Lane Dash',
    description: 'Tap to dodge obstacles. How far can you run?',
    icon: ({ className }) => (
      <div className={className}>
        <FontAwesomeIcon icon={faRoad} style={{ width: '100%', height: '100%' }} />
      </div>
    ),
    options: {
      title: "Lane Dash",
      intro: "Tap left or right to switch lanes. Swipe up to jump. Grab stars to build combos.",
      ctaEnabled: false,
      leaderboard: {
        enabled: true,
        emailRequired: 'optional',
        allowMultipleAttempts: true
      },
      backgroundImage: 'procedural:skyCloudsTopDown',
      backgroundImageName: 'Sky & Clouds',
      gameSettings: {
        playerColor: '#EF4444',
        obstacleColor: '#4F46E5',
        backgroundColor: '#1a1a2e',
        trackColor: '#2a2a3e', // Track/ground fallback when no texture
      },
      playerAnimationFrames: [
        '/assets/library/players/new-characters-svg/character_green_climb_a.svg',
        '/assets/library/players/new-characters-svg/character_green_climb_b.svg',
      ],
      playerJumpImage: '/assets/library/players/new-characters-svg/character_green_climb_a.svg',
      playerSetName: 'Default character',
      collectibleImage: '/assets/library/items/new-svg/star.svg',
      collectibleImageName: 'Star',
      obstacleImage: '/assets/library/obstacles/new-svg/spikes.svg',
      obstacleImageName: 'Spikes',
      collectSound: 'synth:collect-coin',
      jumpSound: 'synth:laneswitch-swoosh',
      laneSwitchSound: 'synth:laneswitch-tick',
      deathSound: '/assets/audio/kenney-impact/impactGeneric_light_000.ogg'
    },
  },
  {
    id: "quizrush",
    category: 'trivia',
    backgroundDirection: null,
    name: "Quiz Rush",
    description: "Timed multiple-choice quiz with arcade scoring.",
    icon: ({ className }) => (
      <div className={className}>
        <FontAwesomeIcon icon={faCircleQuestion} style={{ width: '100%', height: '100%' }} />
      </div>
    ),
    options: {
      title: "Quiz Rush",
      intro: "Answer as many questions as you can. Faster correct answers score more points!",
      ctaEnabled: false,
      leaderboard: {
        enabled: true,
        emailRequired: 'optional',
        allowMultipleAttempts: false, // First score only; replay cannot replace
      },
      timePerQuestion: 20,
      backgroundImage: 'procedural:skyClouds',
      backgroundImageName: 'Sky & Clouds',
      questions: [
        {
          id: 'q-default-1',
          questionText: 'Your first question?',
          image: null,
          optionA: 'Yes',
          optionB: 'No',
          optionC: 'Maybe',
          optionD: 'Skip',
          correctAnswer: 'A',
        },
      ],
      correctSound: 'synth:collect-pop',
      wrongSound: '/assets/audio/kenney-impact/impactGeneric_light_000.ogg',
    },
  },
  {
    id: "completelist",
    category: 'trivia',
    backgroundDirection: null,
    name: 'Complete the List',
    description: 'Name the items on the list before time runs out.',
    icon: ({ className }) => (
      <div className={className}>
        <FontAwesomeIcon icon={faListOl} style={{ width: '100%', height: '100%' }} />
      </div>
    ),
    options: {
      title: "Complete the List",
      intro: "Type your answers to complete the list. How many can you name?",
      ctaEnabled: false,
      leaderboard: {
        enabled: true,
        emailRequired: 'optional',
        allowMultipleAttempts: false,
      },
      softCapSeconds: 180,
      pointsPerItem: 100,
      listTitle: '',
      listItems: [],
      backgroundImage: 'procedural:skyClouds',
      backgroundImageName: 'Sky & Clouds',
      lives: 3,
      correctSound: 'synth:collect-pop',
      wrongSound: '/assets/audio/kenney-impact/impactGeneric_light_000.ogg',
      completeSound: 'synth:collect-coin',
    },
  },
  {
    id: "minigolf",
    category: 'arcade',
    backgroundDirection: null,
    name: 'Mini Golf',
    description: 'Aim and putt across multiple holes. Fewest strokes wins.',
    icon: ({ className }) => (
      <div className={className}>
        <FontAwesomeIcon icon={faFlag} style={{ width: '100%', height: '100%' }} />
      </div>
    ),
    options: {
      title: "Mini Golf",
      intro: "Drag to aim, release to putt. Fewest strokes per hole = highest score.",
      ctaEnabled: false,
      backgroundImage: 'procedural:skyClouds',
      backgroundImageName: 'Sky & Clouds',
      ballImage: null,
      wallImage: null,
      leaderboard: {
        enabled: true,
        emailRequired: 'optional',
        allowMultipleAttempts: true,
        lowerIsBetter: false,
      },
      collectSound: 'synth:collect-pop',
      bounceSound: '/assets/audio/kenney-impact/impactGeneric_light_000.ogg',
      deathSound: '/assets/audio/kenney-impact/impactGeneric_light_000.ogg',
    },
  },
  {
    id: "stacker",
    category: 'arcade',
    backgroundDirection: null,
    bgOrientation: 'vertical', // No parallax scroll, but composes vertically — picker hides horizon-scene bgs
    name: 'Stack Tower',
    description: 'Tap to stack. Time it right or lose your edge.',
    icon: ({ className }) => (
      <div className={className}>
        <FontAwesomeIcon icon={faLayerGroup} style={{ width: '100%', height: '100%' }} />
      </div>
    ),
    options: {
      title: "Stack Tower",
      intro: "Tap to place each block. Line them up perfectly to score big!",
      ctaEnabled: false,
      backgroundImage: 'procedural:skyCloudsTopDown',
      backgroundImageName: 'Sky & Clouds',
      blockImage: null,
      leaderboard: {
        enabled: true,
        emailRequired: 'optional',
        allowMultipleAttempts: true,
      },
      placeSound: 'synth:collect-pop',
      perfectSound: 'synth:collect-coin',
      deathSound: '/assets/audio/kenney-impact/impactGeneric_light_000.ogg',
    },
  },
  {
    id: "snake",
    category: 'arcade',
    backgroundDirection: null,
    name: 'Snake',
    description: 'Eat, grow, survive. The classic never gets old.',
    icon: ({ className }) => (
      <div className={className}>
        <FontAwesomeIcon icon={faWorm} style={{ width: '100%', height: '100%' }} />
      </div>
    ),
    options: {
      title: "Snake",
      intro: "Swipe to steer. Eat to grow. Don't crash into yourself.",
      ctaEnabled: false,
      backgroundImage: 'procedural:skyClouds',
      backgroundImageName: 'Sky & Clouds',
      collectibleImage: '/assets/library/items/new-svg/star.svg',
      collectibleImageName: 'Star',
      snakeSkin: {
        mode: 'simple',
        simple: {
          baseOrientation: { head: 'right' },
          foodUrl: null,
        },
      },
      leaderboard: {
        enabled: true,
        emailRequired: 'optional',
        allowMultipleAttempts: true
      },
      gameSettings: {
        snakeColor: '#5DD4AA',
        foodColor: '#EF4444',
        gridColor: '#E5E7EB',
      },
      collectSound: 'synth:collect-pop',
      deathSound: '/assets/audio/kenney-impact/impactGeneric_light_000.ogg'
    },
  },
];

// Apply preview library so each game type has imageUrl from the central registry.
gameTypes.forEach((gt) => {
  const { primary } = getGameTypePreview(gt.id);
  gt.imageUrl = primary.imageUrl ?? null;
});
